import { useRef, useState, ChangeEvent, useEffect } from 'react';
import styles from '../VerifyEmail/VerifyEmail.module.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { initiateMFA, verifyMFA } from '../../auth/_requests';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { RotateCw } from 'lucide-react';
import { AuthBrandedLoader } from '../Login/AuthBrandedLoader';
import loginStyles from '../Login/Login.module.css';
import { AuthPageShell } from '../Login/AuthPageShell';
import { postLoginTask } from '../../helpers/utils/login';
import { showToast } from '../../helpers/utils/showToast';
import { useMFA } from '../../helpers/hooks/mfaListContext';

const MFAVerificationPhone = () => {
  const { auth, currentUser, saveAuth } = useAuth();
  const { emailEnabled } = useMFA();
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  const [inputValues, setInputValues] = useState(['', '', '', '']);
  const [errorMessage, setErrorMessage] = useState('');
  const codeSentRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(900); // 15 minutes = 900 seconds
  const [resendEnabled, setResendEnabled] = useState(false);

  // Format as mm:ss
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (secondsLeft > 0) {
      timer = setInterval(() => {
        setSecondsLeft((prev) => prev - 1);
      }, 1000);
    } else {
      setResendEnabled(true);
    }
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const initiateMFASession = async () => {
    try {
      setLoading(true);
      await initiateMFA(auth?.data.token || '');
      codeSentRef.current = true;
      setSecondsLeft(900); // reset timer to 15 mins
      setResendEnabled(false);
    } catch (error) {
      console.error('Error sending code:', error);
      const isBlockedPhone = error instanceof Error && error.message === 'phone_number_blocked';
      if (isBlockedPhone) {
        setErrorMessage(
          'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const navigate = useNavigate();

  const handleInputChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.slice(0, 1);
    if (!/^\d$/.test(value)) {
      value = '';
    }
    const newValues = [...inputValues];
    newValues[index] = value;
    setInputValues(newValues);
    if (value.length === 1 && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleVerify = async () => {
    const code = inputValues.join('');
    const isValidCode = code.length === 4 && /^\d{4}$/.test(code);
    if (isValidCode) {
      setErrorMessage('');
      setLoadingVerify(true);
      try {
        const token = auth?.data.token || '';
        const response = await verifyMFA(token, code);
        if (response.message === 'verification_success') {
          showToast({
            message: 'Verification successful!',
            type: 'success',
          });
          setIsEmailVerified(true);
          if (auth) postLoginTask(auth, navigate, saveAuth);
        }
      } catch (error) {
        const isBlockedPhone = error instanceof Error && error.message === 'phone_number_blocked';
        setErrorMessage(
          isBlockedPhone
            ? 'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
            : 'Please enter a valid code'
        );
      } finally {
        setLoadingVerify(false);
      }
    } else {
      setErrorMessage('Please enter a valid code');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (['+', '-'].includes(e.key)) {
      e.preventDefault();
    } else if (e.key === 'Enter') {
      handleVerify();
    } else if (e.key === 'Backspace') {
      if (inputValues[index] === '' && index > 0) {
        inputRefs[index - 1].current?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text');
    const digits = pastedData.replace(/\D/g, '').substring(0, 4).split('');

    const newValues = [...inputValues];
    newValues[0] = digits[0] || '';
    newValues[1] = digits[1] || '';
    newValues[2] = digits[2] || '';
    newValues[3] = digits[3] || '';
    setInputValues(newValues);
    inputRefs[digits.length > 0 ? digits.length - 1 : 0].current?.focus();
  };
  const formatMaskedPhoneNumber = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');

    if (digits.length < 4) return digits;

    const last4 = digits.slice(-4);
    return `(XXX) XXX-${last4}`;
  };

  useEffect(() => {
    const initiateMFASessionOnMount = async () => {
      try {
        setLoading(true);
        await initiateMFA(auth?.data.token || '');
        codeSentRef.current = true;
      } catch (error) {
        console.error('Error sending code:', error);
        const isBlockedPhone = error instanceof Error && error.message === 'phone_number_blocked';
        if (isBlockedPhone) {
          setErrorMessage(
            'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
          );
        }
      } finally {
        setLoading(false);
      }
    };
    initiateMFASessionOnMount();
  }, []);

  if (loading) {
    return <AuthBrandedLoader message='Loading…' />;
  }
  return (
    <AuthPageShell variant='center'>
      <div className={loginStyles.authFlowMax}>
        <motion.main
          className={`${loginStyles.main} ${loginStyles.mainWide} ${styles.verifyFlow}`}
          initial={false}
        >
          <p className={`${styles.emailHeader} text-center w-full`}>Multi-factor authentication</p>

          <div className={styles.emailInner}>
            <p className={styles.emailText}>
              A verification code has been sent to <br />
              <span className='font-bold'>
                {currentUser?.user?.phoneNumber
                  ? `+${currentUser.user.phoneNumberExtension || '1'} ${formatMaskedPhoneNumber(
                      currentUser.user.phoneNumber
                    )}`
                  : ''}
              </span>
            </p>
            <p className={styles.emailText}>Enter the 4‑digit code we just sent to your phone.</p>
            <div className={styles.codeBox}>
              <div className={styles.codeBoxInner}>
                <div className={styles.inputBoxes}>
                  {inputRefs.map((ref, index) => (
                    <input
                      key={index}
                      type='number'
                      className={`${styles.numberBox} `}
                      ref={ref}
                      value={inputValues[index]}
                      onChange={handleInputChange(index)}
                      maxLength={1}
                      min={0}
                      max={9}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onPaste={handlePaste}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    />
                  ))}
                </div>
                {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
              </div>
            </div>
            {codeSentRef.current === true && (
              <div className={styles.sendOuter}>
                {secondsLeft > 0 ? (
                  <p
                    className={`${styles.emailText} ${styles.timerText} ${
                      secondsLeft <= 30 ? styles.warning : ''
                    }`}
                  >
                    The code will expire in{' '}
                    <span className='font-bold'>{formatTime(secondsLeft)}</span>
                  </p>
                ) : (
                  <p className={styles.emailText}>
                    Code expired. Please click below to resend a new one.
                  </p>
                )}
                <button
                  type='button'
                  className={`${styles.resendCode} ${!resendEnabled && styles.disabledResend}`}
                  onClick={resendEnabled ? initiateMFASession : undefined}
                  disabled={!resendEnabled}
                >
                  <RotateCw />
                  <p>Resend code</p>
                </button>
              </div>
            )}
          </div>
          <button
            type='button'
            className={clsx(
              loginStyles.submitBtn,
              (isEmailVerified || inputValues.some((val) => val === '') || loadingVerify) &&
                loginStyles.disableBtn
            )}
            onClick={handleVerify}
            disabled={isEmailVerified || inputValues.some((val) => val === '') || loadingVerify}
          >
            {!loadingVerify && <span className='indicator-label'>Continue</span>}
            {loadingVerify && (
              <span
                className={styles.indicatorProgress}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Please wait...
                <span className={styles.spinner}></span>
              </span>
            )}
          </button>

          {emailEnabled && (
            <button
              type='button'
              onClick={() => {
                navigate('/two-factor-authentication/email');
              }}
              className={loginStyles.forgotPassword}
            >
              Verify with email?
            </button>
          )}
        </motion.main>
      </div>
    </AuthPageShell>
  );
};
export default MFAVerificationPhone;
